import { useState, useEffect } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  Tray, UploadSimple, Users, Shield, SignOut, ChartLine, Buildings,
  Gear, List, X, ChatCircleDots, Coins, ChartLineUp, Receipt, ShareNetwork, Books,
  Calendar as CalendarIcon, Wrench, BookOpen, EnvelopeOpen, ClipboardText,
  CloudLightning, Path, Newspaper,
} from "@phosphor-icons/react";
import { LogoWordmark } from "@/components/Logo";
import { ROLE_LABEL, ROLE_BADGE_CLASS, can } from "@/lib/roles";
import IncidenciasNotifier from "@/components/IncidenciasNotifier";
import AvizorListener from "@/components/AvizorListener";
import ChatDrawer from "@/components/chat/ChatDrawer";
import GlobalSearch from "@/components/GlobalSearch";
import { useChat } from "@/contexts/ChatContext";

const NAV = [
  { to: "/inbox", label: "Bandeja", icon: Tray, mobile: true },
  { to: "/produccion", label: "Producción", icon: ChartLineUp, mobile: false },
  { to: "/rutas", label: "Rutas", icon: Path, mobile: false },
  { to: "/upload", label: "Añadir siniestros", icon: UploadSimple, mobile: false },
  { to: "/chat", label: "Chat", icon: ChatCircleDots, mobile: false, badgeKey: "chat" },
  { to: "/calendar", label: "Agenda", icon: CalendarIcon, mobile: false },
  { to: "/repositorio", label: "Repositorio", icon: Books, mobile: false },
  { to: "/meteo", label: "Meteo", icon: CloudLightning, mobile: false },
  { to: "/news-climatic", label: "Noticias", icon: Newspaper, mobile: false },
  { to: "/analytics", label: "Analítica", icon: ChartLine, mobile: false, end: true },
];

const ADMIN_NAV = [
  { to: "/admin/peritos", label: "Usuarios", icon: Users, allow: can.manageUsers },
  { to: "/admin/control-gestiones", label: "Control de equipo", icon: ClipboardText, allow: can.approveMinute },
  { to: "/admin/aseguradoras", label: "Aseguradoras", icon: Shield, allow: () => true },
  { to: "/admin/reparadores", label: "Reparadores", icon: Wrench, allow: () => true },
  { to: "/admin/gabinetes", label: "Gabinetes", icon: Buildings, allow: () => true },
  { to: "/admin/liquidaciones-aseguradoras", label: "Liquidaciones", icon: Coins, allow: can.rootAdmin },
  { to: "/admin/clientes-facturacion", label: "Clientes", icon: Buildings, allow: can.rootAdmin },
  { to: "/admin/facturas", label: "Facturas", icon: Receipt, allow: can.rootAdmin },
  { to: "/admin/rendimiento", label: "Rendimiento", icon: ChartLineUp, allow: can.rootAdmin },
  { to: "/admin/partners", label: "Gabinetes partner", icon: ShareNetwork, allow: can.rootAdmin },
  { to: "/admin/email-intake", label: "Email entrante", icon: EnvelopeOpen, allow: can.rootAdmin },
  { to: "/admin/configuracion", label: "Configuración", icon: Gear, allow: can.downloadAiTransparency },
];

// Para la barra inferior móvil dejamos solo lo que el perito necesita en
// campo: Bandeja (de ahí abre los expedientes) y un acceso rápido a la
// analítica con sus datos.
const MOBILE_NAV = [
  { to: "/analytics", label: "Analítica", icon: ChartLine, end: true },
  { to: "/inbox", label: "Bandeja", icon: Tray },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { totalUnread } = useChat();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [solicitudesOpen, setSolicitudesOpen] = useState(0);
  const [incidenciasOverdue, setIncidenciasOverdue] = useState(0);
  const [recordatoriosOverdue, setRecordatoriosOverdue] = useState(0);

  const isColector = !!(user?.is_colector || user?.role === "admin");
  const isGestor = ["admin", "gestor_senior", "gestor_siniestros"].includes(user?.role);
  const showGestoresNav = isColector || isGestor;
  // Tramitador telemático: tramitación remota → sin agenda ni rutas.
  const isTramitadorTelematico = user?.role === "tramitador_telematico";

  // Polling (cada 60s):
  //  - Colectores → contador de solicitudes-doc.
  //  - Gestores/admin → contador de incidencias vencidas (badge rojo).
  //  - Todos los usuarios → contador de recordatorios personales vencidos.
  useEffect(() => {
    if (!user) { setSolicitudesOpen(0); setIncidenciasOverdue(0); setRecordatoriosOverdue(0); return; }
    let cancelled = false;
    const tick = () => {
      if (isColector) {
        api.get("/solicitudes-doc/my-queue/count")
          .then((r) => { if (!cancelled) setSolicitudesOpen(r.data?.open || 0); })
          .catch(() => { /* sin red u offline */ });
      }
      if (isGestor) {
        api.get("/gestores/incidencias/count")
          .then((r) => { if (!cancelled) setIncidenciasOverdue(r.data?.overdue || 0); })
          .catch(() => { /* sin red */ });
      }
      api.get("/recordatorios/count")
        .then((r) => { if (!cancelled) setRecordatoriosOverdue(r.data?.overdue || 0); })
        .catch(() => { /* sin red */ });
    };
    tick();
    const t = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user, isColector, isGestor]);

  const initials = (user?.name || "")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const linkBase = "flex items-center gap-2.5 px-2.5 py-[7px] rounded-lg text-[13px] font-medium transition-colors duration-150 group";
  const linkActive = "bg-sidebar-2 text-sidebar-fg";
  const linkIdle = "text-sidebar-fg-soft hover:bg-sidebar-2";

  const renderNav = (closeOnClick = false) => {
    // Filtrar accesos no aplicables al tramitador telemático (sin agenda/rutas).
    const baseNav = isTramitadorTelematico
      ? NAV.filter((n) => !["/calendar", "/rutas"].includes(n.to))
      : NAV;
    const navItems = [
      ...baseNav.slice(0, 4),
      ...(showGestoresNav
        ? [{ to: "/gestores", label: "Gestores", icon: ClipboardText, mobile: false, badgeKey: "gestores" }]
        : []),
      ...baseNav.slice(4),
    ];
    return (
    <nav className="px-3 py-2 flex-1 flex flex-col gap-px overflow-y-auto">
      <div className="text-[10px] uppercase tracking-widest text-sidebar-fg-muted px-2.5 pt-1 pb-1 font-semibold">
        General
      </div>
      {navItems.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={() => closeOnClick && setDrawerOpen(false)}
          data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
          className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
        >
          {({ isActive }) => (
            <>
              <n.icon className={`w-4 h-4 transition-colors ${isActive ? "text-brand" : "text-sidebar-fg-soft group-hover:text-sidebar-fg"}`} weight="regular" />
              <span className="flex-1">{n.label}</span>
              {n.badgeKey === "chat" && totalUnread > 0 && (
                <span className="bg-status-error-fg text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                  {totalUnread > 99 ? "99+" : totalUnread}
                </span>
              )}
              {n.badgeKey === "gestores" && (solicitudesOpen > 0 || incidenciasOverdue > 0 || recordatoriosOverdue > 0) && (
                <span className={`text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center ${
                  incidenciasOverdue > 0 || recordatoriosOverdue > 0 ? "bg-rose-600" : "bg-amber-500"
                }`}>
                  {(() => {
                    const total = solicitudesOpen + incidenciasOverdue + recordatoriosOverdue;
                    return total > 99 ? "99+" : total;
                  })()}
                </span>
              )}
            </>
          )}
        </NavLink>
      ))}

      <a
        href="/docs/MANUAL_Ekspertium.pdf"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="nav-manual"
        className={`${linkBase} ${linkIdle}`}
        title="Abrir manual de usuario (PDF)"
      >
        <BookOpen className="w-4 h-4 text-sidebar-fg-soft group-hover:text-sidebar-fg" weight="regular" />
        <span className="flex-1">Manual</span>
        <span className="text-[9px] uppercase tracking-wider text-sidebar-fg-muted font-semibold">PDF</span>
      </a>

      {(() => {
        const visible = ADMIN_NAV.filter((n) => n.allow(user));
        if (visible.length === 0) return null;
        return (
          <>
            <div className="text-[10px] uppercase tracking-widest text-sidebar-fg-muted px-2.5 pt-3 pb-1 font-semibold">
              {can.rootAdmin(user) ? "Administración" : "Consulta"}
            </div>
            {visible.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => closeOnClick && setDrawerOpen(false)}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
              >
                {({ isActive }) => (
                  <>
                    <n.icon className={`w-4 h-4 transition-colors ${isActive ? "text-brand" : "text-sidebar-fg-soft group-hover:text-sidebar-fg"}`} weight="regular" />
                    {n.label}
                  </>
                )}
              </NavLink>
            ))}
          </>
        );
      })()}
    </nav>
  );};

  // `userCard` lo usan dos sitios:
  //   - Sidebar desktop  → `userCard()`   (no se cierra ningún drawer)
  //   - Drawer móvil     → `userCard(true)` (queremos cerrar el drawer)
  // Antes este componente accedía a una variable `closeOnClick` que no
  // existía en el ámbito (residuo del refactor inicial). Lo expongo
  // ahora como parámetro explícito.
  const userCard = (closeOnClick = false) => (
    <div className="px-3 py-2.5 border-t border-sidebar-border">
      <div className="flex items-center gap-2.5 px-1">
        <NavLink
          to="/mi-perfil"
          onClick={() => closeOnClick && setDrawerOpen(false)}
          data-testid="nav-mi-perfil"
          className="flex items-center gap-2.5 flex-1 min-w-0 hover:opacity-80 transition-opacity"
          title="Ir a mi perfil"
        >
          <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center text-[11px] font-semibold flex-shrink-0">
            {initials || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold truncate text-sidebar-fg leading-tight">{user?.name}</div>
            <div className="text-[11px] text-sidebar-fg-soft truncate leading-tight mt-0.5">
              {ROLE_LABEL[user?.role] || user?.role}
            </div>
          </div>
        </NavLink>
        <button
          onClick={logout}
          data-testid="logout-button"
          title="Cerrar sesión"
          className="p-1.5 hover:bg-sidebar-2 rounded-md text-sidebar-fg-soft hover:text-status-error-fg transition-colors"
        >
          <SignOut className="w-4 h-4" weight="regular" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* ============= SIDEBAR DESKTOP (fijo durante el scroll) ============= */}
      <aside className="hidden md:flex w-60 sidebar-dark flex-col border-r border-sidebar-border sticky top-0 h-screen self-start z-30">
        <div className="h-[48px] flex items-center justify-between px-4 border-b border-sidebar-border flex-shrink-0">
          <LogoWordmark small />
          <ChatDrawer />
        </div>
        {renderNav(false)}
        {userCard(false)}
      </aside>

      {/* ============= TOPBAR MÓVIL ============= */}
      <header className="md:hidden fixed top-0 inset-x-0 z-30 h-12 sidebar-dark flex items-center justify-between px-3 border-b border-sidebar-border">
        <LogoWordmark small />
        <div className="flex items-center gap-1">
          <ChatDrawer />
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="mobile-menu-button"
            className="p-2 text-sidebar-fg-soft hover:text-sidebar-fg"
            aria-label="Abrir menú"
          >
            <List className="w-5 h-5" weight="bold" />
          </button>
        </div>
      </header>

      {/* ============= DRAWER MÓVIL ============= */}
      {drawerOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/60"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="md:hidden fixed top-0 left-0 z-50 h-full w-72 sidebar-dark flex flex-col border-r border-sidebar-border animate-in slide-in-from-left">
            <div className="h-12 flex items-center justify-between px-3 border-b border-sidebar-border">
              <LogoWordmark small />
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-2 text-sidebar-fg-muted"
                aria-label="Cerrar menú"
              >
                <X className="w-5 h-5" weight="bold" />
              </button>
            </div>
            {renderNav(true)}
            {userCard(true)}
          </aside>
        </>
      )}

      {/* ============= MAIN ============= */}
      <main className="flex-1 min-w-0 flex flex-col relative pt-12 md:pt-0 pb-14 md:pb-0">
        {/* Topbar global: búsqueda fija siempre visible (desktop + mobile) */}
        {/* z-40 para que su stacking context quede por encima de los
            sticky headers de páginas (z-20). Sin esto, el dropdown de
            resultados del GlobalSearch (z-50 relativo) quedaba tapado
            por el header "Administración > Gabinetes · N" y similares. */}
        <div className="sticky top-12 md:top-0 z-40 h-[54px] bg-white border-b border-border flex items-center px-3 md:px-6 gap-4">
          <GlobalSearch />
        </div>
        <IncidenciasNotifier />
        <AvizorListener />
        <Outlet />
      </main>

      {/* ============= BOTTOM NAV MÓVIL ============= */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 h-14 bg-white border-t border-border flex items-stretch">
        {MOBILE_NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`mobile-nav-${n.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold transition-colors ${
                isActive ? "text-brand" : "text-muted hover:text-foreground"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <n.icon className="w-5 h-5" weight={isActive ? "fill" : "bold"} />
                {n.label}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
